import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { RenderControlData } from '../../RenderControlData.js';

const defaultTitle = "THE FUTURE OF FILMMAKING IS HERE";
const defaultButtonLabel = "JOIN THE WAITLIST";
const defaultButtonHref = "/waitlist";
const defaultBackgroundImage = "https://curiousrefuge.com/wp-content/uploads/2023/12/hero-banner.webp";
const defaultOverlayColor = "rgba(0,0,0,0.6)"; // Darkened overlay color as per reference
const defaultTextColor = '#ffffff';
const defaultButtonBgColor = '#F2C94C';
const defaultButtonTextColor = '#000000';

const HeroBanner = ({
  title = defaultTitle,
  buttonLabel = defaultButtonLabel,
  buttonHref = defaultButtonHref,
  backgroundImage = defaultBackgroundImage,
  overlayColor = defaultOverlayColor,
  textColor = defaultTextColor,
  buttonBgColor = defaultButtonBgColor,
  buttonTextColor = defaultButtonTextColor,
}) => {
  // State for navigation link hover effect
  const [hoveredNavLink, setHoveredNavLink] = React.useState(null);

  // Styles for the main section container
  const sectionStyle = {
    position: 'relative',
    height: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: '1.5rem',
    marginTop: '4.5rem', // Added margin-top to push below fixed header
    marginBottom: '0', // Set margin-bottom to 0 as cookie banner is fixed at bottom
  };

  // Styles for the background image
  const backgroundStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    zIndex: 0,
  };

  // Styles for the semi-transparent overlay
  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: overlayColor,
    zIndex: 1,
  };

  // Styles for the content (title and button)
  const contentStyle = {
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
    color: textColor,
    padding: '1rem',
  };

  // Styles for the main title
  const titleStyle = {
    fontSize: 'clamp(2rem, 5vw, 4rem)', // Updated font size
    fontWeight: 'bold',
    lineHeight: '1.1',
    marginBottom: '1.5rem',
    textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
    letterSpacing: '0.06em', // Updated letter spacing
  };

  // Styles for the call-to-action button
  const buttonCustomStyle = {
    backgroundColor: buttonBgColor,
    color: buttonTextColor,
    borderColor: buttonBgColor,
    padding: '1rem 3rem', // Updated padding
    fontSize: '1.2rem',
    fontWeight: 'bold',
    borderRadius: '2rem',
    marginTop: '2.5rem', // Added margin-top for vertical spacing
  };

  const navLinksData = [
    { label: "Courses", href: "#courses" },
    { label: "Community", href: "#community" },
    { label: "Resources", href: "#resources" },
    { label: "About", href: "#about" },
    { label: "Course Login", href: "#login" },
  ];

  return (
    <>
      {/* Fixed Header/Navigation Bar */}
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1050,
          padding: '1rem 2rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        className="d-flex justify-content-between align-items-center"
      >
        <span
          style={{
            color: '#ffffff',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            letterSpacing: '-0.02em',
          }}
        >
          curious refuge
        </span>
        <nav className="d-flex align-items-center">
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              gap: '1.5rem',
            }}
          >
            {navLinksData.map((link, index) => (
              <li key={index}>
                <a
                  href={link.href}
                  style={{
                    color: hoveredNavLink === index ? '#F2C94C' : '#e0e0e0',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    transition: 'color 0.3s ease',
                  }}
                  onMouseEnter={() => setHoveredNavLink(index)}
                  onMouseLeave={() => setHoveredNavLink(null)}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <Button
            style={{
              backgroundColor: '#212b48',
              color: '#ffffff',
              borderRadius: '0.5rem',
              padding: '0.6rem 1.5rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              border: 'none',
              marginLeft: '2rem', // Spacing from nav links
            }}
          >
            START FREE
          </Button>
        </nav>
      </header>

      {/* Hero Banner Section */}
      <section style={sectionStyle}>
        <div style={backgroundStyle} aria-hidden="true" />
        <div style={overlayStyle} aria-hidden="true" />

        <Container fluid className="h-100 d-flex align-items-center justify-content-center">
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8}>
              <div style={contentStyle}>
                <h1 style={titleStyle}>{title}</h1>
                <Button href={buttonHref} style={buttonCustomStyle}>
                  {buttonLabel}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Fixed Cookie Consent Banner */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1050,
          padding: '1.2rem 2rem',
          backgroundColor: 'rgb(243, 242, 233)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
        }}
        className="d-flex justify-content-between align-items-center flex-wrap"
      >
        <p
          style={{
            color: 'rgb(51, 51, 51)',
            fontSize: '0.9rem',
            margin: 0,
            flexGrow: 1,
            marginRight: '1rem',
          }}
        >
          We use cookies to improve your experience on our website. By continuing to browse, you agree to our use of cookies.
        </p>
        <div className="d-flex gap-2">
          <Button
            style={{
              backgroundColor: '#212b48',
              color: '#ffffff',
              borderRadius: '0.5rem',
              padding: '0.6rem 1.2rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              border: 'none',
            }}
          >
            LEARN MORE
          </Button>
          <Button
            style={{
              backgroundColor: '#F2C94C',
              color: '#000000',
              borderRadius: '0.5rem',
              padding: '0.6rem 1.2rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              border: 'none',
            }}
          >
            ACCEPT
          </Button>
        </div>
      </div>
    </>
  );
};

class HeroBannerData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    title: String,
    buttonLabel: String,
    buttonHref: String,
    backgroundImage: String,
    overlayColor: String,
    textColor: String,
    buttonBgColor: String,
    buttonTextColor: String,
  });

  constructor(data = {}) {
    super(data);
    Object.assign(this, data);
  }
}

export { HeroBanner, HeroBannerData };
export default HeroBanner;